import { getLocale, IbhFilterKeys } from '@hub/libs/constants';
import { useTranslation } from 'next-i18next';
import { FilterSelectAutocomplete } from '@hub/common/inputs/FilterSelectAutocomplete';
import type {
  IbhFilterControl,
  IbhFilterValues,
  IbhFilterValuesStore,
} from '@hub/hooks/states/useIbhFilterValuesStore';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { IbhEventCategory, TrackedIbhEventActions } from '@hub/bearing-hub/common/tracking.config';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { useRouter } from 'next/router';
import { Checkbox } from '@hub/common/Checkbox/Checkbox';
import type { IbhProductionMethodResponseDto } from '@igus/iglidur-bearing-hub-api';
import { useEffect } from 'react';
import { SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import type { IbhIcnProcedure, IbhProcedure } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { getProcedureText } from '@hub/bearing-hub/common/utils';
import { cncProcedures, methodSpecificProcedures, printProcedures } from '@hub/bearing-hub/common/constants';
import { type MaterialFilterOptions, usMarketMaterials } from '@hub/bearing-hub/common/materials/useMaterials';

export interface MethodAndMaterialSectionProps {
  materialFilterOptions: MaterialFilterOptions[];
  productionMethods: IbhProductionMethodResponseDto[];
  filterValues: IbhFilterValues;
  filterControl: IbhFilterControl;
  setFilterValue: IbhFilterValuesStore['setFilterValue'];
  availableParts: number;
  isCollapsible?: boolean;
  setActiveCompareMaterialsModal: (state: boolean) => void;
  setActiveIglideStandardModal: (state: boolean) => void;
  activeCompareMaterialsModal: boolean;
  activeIglideStandardModal: boolean;
}

export const MethodAndMaterialSection = ({
  materialFilterOptions,
  productionMethods,
  filterValues,
  filterControl,
  setFilterValue,
  isCollapsible = false,
  availableParts,
  setActiveCompareMaterialsModal,
  activeCompareMaterialsModal,
  activeIglideStandardModal,
  setActiveIglideStandardModal,
}: MethodAndMaterialSectionProps) => {
  const { t } = useTranslation(['category/productFilter', 'common']);
  const { CustomEvent } = usePiwikPro();
  const router = useRouter();
  const locale = getLocale(router);
  const selectedMaterials = (filterValues.material as string[]) || [];
  const standardMaterials = materialFilterOptions
    .filter(({ value }) => usMarketMaterials.includes(value))
    .map(({ value }) => value);
  const useUsMaterials =
    !!filterValues[IbhFilterKeys.US_STANDARD_MATERIALS] ||
    (SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) &&
      standardMaterials.length > 0 &&
      selectedMaterials.length === standardMaterials.length &&
      standardMaterials.every(material => selectedMaterials.includes(material)));
  const couldResetMaterialValues =
    selectedMaterials?.length === 1 || (selectedMaterials?.length > 1 && availableParts === 0);

  useEffect(() => {
    if (useUsMaterials && !filterValues[IbhFilterKeys.US_STANDARD_MATERIALS]) {
      setFilterValue(IbhFilterKeys.US_STANDARD_MATERIALS, true);
    }
    const currentSelectedMaterials = (filterValues.material as string[]) || [];
    const updatedSelectedMaterials = currentSelectedMaterials.filter(material =>
      materialFilterOptions.find(({ value }) => value === material)
    );
    if (updatedSelectedMaterials.length !== currentSelectedMaterials.length) {
      setFilterValue(IbhFilterKeys.MATERIAL, updatedSelectedMaterials, { preserveStandardMaterials: true });
    }
  }, [materialFilterOptions, filterValues, useUsMaterials, setFilterValue]);

  const productionMethodOptions = productionMethods
    .filter(({ selectable }) => selectable)
    .flatMap(({ productionMethod }) => {
      const is3DPrint = productionMethod === IbhProductionMethods.THREEDIMENSIONALPRINT;

      const getOptions = (availableProcedures: IbhIcnProcedure[] | IbhProcedure[]) => {
        return availableProcedures.map((procedure: IbhIcnProcedure | IbhProcedure) => ({
          label: getProcedureText(productionMethod, t, procedure),
          value: `${procedure}_${productionMethod}`,
        }));
      };

      if (productionMethod === IbhProductionMethods.SEMIFINISHED) {
        return getOptions(cncProcedures);
      }

      if (is3DPrint) {
        return getOptions(printProcedures);
      }

      return [
        {
          label: getProcedureText(productionMethod, t),
          value: productionMethod,
        },
      ];
    });

  const selectedProductionMethodValues = () => {
    const currentSelectedFilterMethods =
      (filterValues[IbhFilterKeys.PRODUCTION_METHOD] as IbhProductionMethods[] | undefined) || [];
    const currentFilterProcedures = filterValues[IbhFilterKeys.PROCEDURES] || [];
    const productionMethodOptionsMap = new Map(productionMethodOptions.map(option => [option.value, option]));

    const getFoundOptions = <T extends IbhProcedure | IbhIcnProcedure>(
      method: IbhProductionMethods,
      procedures: T[]
    ) => {
      return (currentFilterProcedures as T[])
        .filter(procedure => procedures.includes(procedure))
        .map(procedure => {
          const combinedValue = `${procedure}_${method}`;
          return productionMethodOptionsMap.get(combinedValue);
        })
        .filter(option => option !== undefined);
    };

    return currentSelectedFilterMethods.flatMap(method => {
      let foundOptions: { label: string; value: string }[] = [];

      if (method === IbhProductionMethods.THREEDIMENSIONALPRINT) {
        foundOptions = getFoundOptions<IbhProcedure>(IbhProductionMethods.THREEDIMENSIONALPRINT, printProcedures);
      } else if (method === IbhProductionMethods.SEMIFINISHED) {
        foundOptions = getFoundOptions<IbhIcnProcedure>(IbhProductionMethods.SEMIFINISHED, cncProcedures);
      } else {
        const option = productionMethodOptionsMap.get(method);
        if (option) {
          foundOptions.push(option);
        }
      }

      return foundOptions;
    });
  };

  const handleProductionMethodChange = (selectedValuesFromAutocomplete: string[]) => {
    const newProductionMethodFilterValuesSet = new Set<string>();
    const proceduresSelectedSet = new Set<IbhProcedure | IbhIcnProcedure>();

    selectedValuesFromAutocomplete.forEach(selectedValue => {
      const parts = selectedValue.split('_');
      const potentialProcedure = parts[0];
      const potentialMethod = parts.slice(1).join('_') as IbhProductionMethods;

      if (
        parts.length > 1 &&
        potentialMethod === IbhProductionMethods.THREEDIMENSIONALPRINT &&
        printProcedures.includes(potentialProcedure as IbhProcedure)
      ) {
        newProductionMethodFilterValuesSet.add(IbhProductionMethods.THREEDIMENSIONALPRINT);
        proceduresSelectedSet.add(potentialProcedure as IbhProcedure);
      } else if (
        parts.length > 1 &&
        potentialMethod === IbhProductionMethods.SEMIFINISHED &&
        cncProcedures.includes(potentialProcedure as IbhIcnProcedure)
      ) {
        newProductionMethodFilterValuesSet.add(IbhProductionMethods.SEMIFINISHED);
        proceduresSelectedSet.add(potentialProcedure as IbhProcedure);
      } else {
        newProductionMethodFilterValuesSet.add(selectedValue);
        methodSpecificProcedures[selectedValue as IbhProductionMethods].forEach(procedure => {
          proceduresSelectedSet.add(procedure as IbhProcedure);
        });
      }
    });

    const finalProductionMethods = Array.from(newProductionMethodFilterValuesSet);
    const finalProcedures = Array.from(proceduresSelectedSet);

    setFilterValue(IbhFilterKeys.PRODUCTION_METHOD, finalProductionMethods);

    if (finalProcedures.length > 0) {
      setFilterValue(IbhFilterKeys.PROCEDURES, finalProcedures);
    } else {
      setFilterValue(IbhFilterKeys.PROCEDURES, undefined);
    }
  };

  return (
    <>
      <div data-testid={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.PRODUCTION_METHOD}`}>
        <FilterSelectAutocomplete
          key={`select_filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.PRODUCTION_METHOD}`}
          id={`select_filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.PRODUCTION_METHOD}`}
          placeholder={t(`FILTERS.PLACEHOLDER.PRODUCTION_METHOD`, { ns: 'bearing-hub/bearingHub' })}
          hideOptionsModel
          options={productionMethodOptions}
          value={selectedProductionMethodValues()}
          isMulti
          onChange={handleProductionMethodChange}
        />
      </div>
      <div data-testid={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.MATERIAL}`}>
        <FilterSelectAutocomplete
          key={`select_filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.MATERIAL}`}
          id={`select_filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.MATERIAL}`}
          placeholder={t(`FILTERS.PLACEHOLDER.MATERIAL`, { ns: 'bearing-hub/bearingHub' })}
          hideOptionsModel
          options={materialFilterOptions}
          filterOptions={(option: { label: string; value: string }, materialSearchTerm: string) => {
            const sanitize = (label: string): string => label.replace('®', '');
            return sanitize(option.label).toLowerCase().includes(sanitize(materialSearchTerm.trim()).toLowerCase());
          }}
          value={materialFilterOptions.filter(material => selectedMaterials.includes(material.value))}
          isMulti
          onChange={value => {
            setFilterValue(IbhFilterKeys.MATERIAL, value);
          }}
        />
        {SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) && (
          <Checkbox
            disabled={(!standardMaterials.length && !useUsMaterials) || !!filterControl[IbhFilterKeys.PTFE_FREE]}
            id={`filter_${isCollapsible ? 'collapsible_' : ''}usStandardMaterials`}
            value={useUsMaterials}
            onChange={newValue => {
              setFilterValue(IbhFilterKeys.MATERIAL, newValue ? standardMaterials : []);
              setFilterValue(IbhFilterKeys.US_STANDARD_MATERIALS, newValue || undefined);
            }}
            label={t(`FILTERS.LABELS.US_STANDARD_MATERIALS`, { ns: 'bearing-hub/bearingHub' })}
          />
        )}
        <button
          type="button"
          className="text-[#D66F00] font-medium mb-4"
          data-testid={couldResetMaterialValues ? 'reset_compare_materials' : 'compare_materials'}
          onClick={() => {
            if (couldResetMaterialValues) {
              setFilterValue(IbhFilterKeys.MATERIAL, []);
            } else {
              CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.compareMaterialsOpen);
              setActiveCompareMaterialsModal(!activeCompareMaterialsModal);
            }
          }}
        >
          {couldResetMaterialValues
            ? t(`FILTERS.SECTIONS.RESET_MATERIAL_FILTER`, { ns: 'bearing-hub/bearingHub' })
            : t(`FILTERS.SECTIONS.COMPARE_MATERIALS`, { ns: 'bearing-hub/bearingHub' })}
        </button>
        {locale === 'en-US' && (
          <div>
            <button
              data-testid="iglide_standard"
              type="button"
              className="text-[#D66F00] space-x-3 font-medium mb-8"
              onClick={() => {
                setActiveIglideStandardModal(!activeIglideStandardModal);
              }}
            >
              <Icon size={7} color={IconColor.ORANGE} id={IconID.PDF_FILES} />
              <span>{t(`FILTERS.SECTIONS.IGLIDE_STANDARDS`, { ns: 'bearing-hub/bearingHub' })}</span>
            </button>
          </div>
        )}
      </div>
      <>
        <Checkbox
          disabled={!!filterControl[IbhFilterKeys.PFAS_FREE]}
          key={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.PFAS_FREE}`}
          id={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.PFAS_FREE}`}
          value={
            !!filterControl[IbhFilterKeys.PFAS_FREE] === true
              ? false
              : !!(filterValues[IbhFilterKeys.PFAS_FREE] as boolean | undefined)
          }
          onChange={newValue => {
            setFilterValue(IbhFilterKeys.PFAS_FREE, newValue || undefined);
          }}
          label={t(`FILTERS.LABELS.PFAS_FREE`, { ns: 'bearing-hub/bearingHub' })}
        />
        <Checkbox
          disabled={!!filterControl[IbhFilterKeys.PTFE_FREE]}
          key={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.PTFE_FREE}`}
          id={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.PTFE_FREE}`}
          value={
            !!filterControl[IbhFilterKeys.PTFE_FREE] === true
              ? false
              : !!(filterValues[IbhFilterKeys.PTFE_FREE] as boolean | undefined)
          }
          onChange={newValue => {
            setFilterValue(IbhFilterKeys.PTFE_FREE, newValue || undefined);
          }}
          label={t(`FILTERS.LABELS.PTFE_FREE`, { ns: 'bearing-hub/bearingHub' })}
        />
        <Checkbox
          disabled={!!filterControl[IbhFilterKeys.HEAVY_DUTY]}
          key={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.HEAVY_DUTY}`}
          id={`filter_${isCollapsible ? 'collapsible_' : ''}${IbhFilterKeys.HEAVY_DUTY}`}
          value={
            !!filterControl[IbhFilterKeys.HEAVY_DUTY] === true
              ? false
              : !!(filterValues[IbhFilterKeys.HEAVY_DUTY] as boolean | undefined)
          }
          onChange={newValue => {
            setFilterValue(IbhFilterKeys.HEAVY_DUTY, newValue || undefined);
          }}
          label={t(`COMPARE_MATERIALS_MODAL.HEAVY_DUTY`, {
            defaultValue: 'Heavy-duty',
            ns: 'bearing-hub/bearingHub',
          })}
        />
      </>
    </>
  );
};
